<script setup lang="ts">
import { computedAsync } from '@vueuse/core';

import { DealCodeTag } from '@apps/main/new-desktop/modules/dealCode';

import { AppLinkButton } from '@libs/ui';
import { isExperimentAsync } from '@libs/feature-toggle';

import type {
  HotelSelectionCardContext,
  HotelSelectionCardSummaryData,
} from '../models';
import { TripIndicator } from '../../../modules/tripIndicator';

interface HotelSelectionCardSummaryProps {
  data: HotelSelectionCardSummaryData;
  context: HotelSelectionCardContext;
}

const props = defineProps<HotelSelectionCardSummaryProps>();

interface HotelSelectionCardSummaryEmits {
  (eventName: 'peek-price'): void;
}

const emit = defineEmits<HotelSelectionCardSummaryEmits>();

const isMoreDatesAndPricesCopyInHotelSelectionExperiment = computedAsync(
  () => isExperimentAsync('more_dates_and_prices_copy_in_hotel_selection'),
  false
);
</script>

<template>
  <div class="hotel-selection-card-summary">
    <div class="details-section">
      <TripIndicator
        v-if="props.data.recipeIngredientTypes.length > 1"
        :ingredient-types="props.data.recipeIngredientTypes"
        :active-ingredient-index="
          props.data.recipeWithoutOwnTransportIngredientIndex
        "
      />

      <div class="more-dates-and-prices">
        <AppLinkButton
          :text="
            isMoreDatesAndPricesCopyInHotelSelectionExperiment
              ? $t('planner.total_price_and_dates')
              : $t('planner.more_dates_and_prices')
          "
          size="s"
          @click="emit('peek-price')"
        />
      </div>
    </div>
    <div class="price-section">
      <DealCodeTag
        v-if="props.data.grossPrice"
        :wrap="true"
        class="campaign-code-tag"
      />
      <div class="prices">
        <div v-if="props.data.grossPrice" class="gross-price">
          {{ props.data.grossPrice }}
        </div>
        <div
          class="total-price"
          :class="{ 'with-discount': props.data.grossPrice }"
        >
          {{ props.data.totalPrice }}
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.hotel-selection-card-summary {
  width: 100%;
  height: 100%;

  display: flex;
  justify-content: space-between;

  .details-section {
    display: flex;
    gap: var(--spacing-4);
    flex-direction: column;
    align-items: flex-start;

    .more-dates-and-prices {
      display: flex;
      white-space: nowrap;
    }

    .pax {
      @include font-label-s-light;
      color: var(--color-neutral-70);
    }
  }

  .price-section {
    display: flex;
    flex-direction: column;
    gap: var(--spacing-2);

    .prices {
      align-self: flex-end;
      display: flex;
      align-items: center;
      gap: var(--spacing-8);

      .gross-price {
        @include font-body-s-bold;
        color: var(--color-neutral-70);
        text-decoration: line-through;
      }

      .total-price {
        @include font-heading-6;
        color: var(--color-neutral-70);

        &.with-discount {
          color: var(--color-error-dark);
        }
      }
    }
  }

  .campaign-code-tag {
    margin-top: var(--spacing-4);
  }
}

// NOTE: min-width is the same as the CARD_WIDTH_BREAKPOINT const in HotelSelectionDynamicScroller.vue file, remember to keep it always equal
@container hotelCard (min-width: 690px) {
  .hotel-selection-card-summary {
    width: 100%;
    height: 100%;

    display: flex;
    flex-direction: column;
    justify-content: flex-end;
    align-items: flex-end;

    .details-section {
      align-items: flex-end;
      gap: var(--spacing-8);
    }

    .price-section {
      margin-top: var(--spacing-32);
      gap: var(--spacing-8);

      .prices {
        display: flex;
        align-items: flex-end;
        flex-direction: column;
        gap: var(--spacing-2);
      }
    }
  }
}
</style>
